// contact.js
//
// Structured page content for the Contact_Page. Intro copy, the company contact
// details (phone, email, office location), map configuration, form labels, and
// SEO metadata live here rather than in component templates so changes need no
// redesign (Requirements 11.2, 10.1, 7.1, 7.2).
//
// `ContactPage.vue` and `ContactForm.vue` import this module. Contact details
// are carried over from the existing `ContactUs.vue` component.

/**
 * The Contact_Page content model.
 *
 * @typedef {Object} ContactContent
 * @property {Object} intro    - page heading / lead copy
 * @property {Object} details  - phone, email, office location (Requirement 7.2)
 * @property {Array}  people    - named contacts carried over from ContactUs.vue
 * @property {Object} form     - form heading and per-field labels (Requirement 7.1)
 * @property {import('./seo').PageSeo} seo - per-page SEO metadata
 */

export const contact = {
  intro: {
    heading: 'Contact us',
    body:
      'Tell us about your project or request a quote. Our team will get back to you as soon as we can.'
  },

  // General company contact details, shown in the "Get in touch" block
  // alongside the enquiry form (Requirement 7.2).
  details: {
    // The Melbourne office number, matching the `melbourne` entry in `offices`
    // below. Previously a personal mobile, which is still listed under `people`.
    phone: '0416 018 826',
    email: 'projects@thinkspatial.com.au'
  },

  // Office locations rendered as cards on the Contact page. Each office shows a
  // title, address, and (optionally) its own phone/email, next to an embedded
  // map. Set `showMap: false` on any office to render the address without a map.
  //
  // `coordinates` (lng/lat) drive both the embedded OpenStreetMap view and the
  // "Get directions" link, so no map access token is required.
  //
  // NOTE: the Darwin office is currently a private house, so its address
  // intentionally omits a street number (street/suburb only).
  officesHeading: 'Our offices',
  officesSubheading:
    'Visit us or reach out to the team at your nearest ThinkSpatial office.',
  offices: [
    {
      id: 'melbourne',
      title: 'Melbourne Office',
      addressLines: ['Suite 4, 64 Sutton Street', 'North Melbourne, VIC 3051'],
      phone: '0416 018 826',
      email: 'yliu@thinkspatial.com.au',
      showMap: true,
      // From the legacy ContactUs.vue marker.
      coordinates: { lng: 144.938384, lat: -37.7908432 }
    },
    {
      id: 'brisbane',
      title: 'Brisbane Office',
      addressLines: ['1/82 Frederick Street', 'Northgate, QLD 4013'],
      phone: '0488 330 916',
      email: 'plazdins@thinkspatial.com.au',
      showMap: true,
      // Approximate coordinates for Frederick Street, Northgate QLD 4013.
      coordinates: { lng: 153.0715, lat: -27.3966 }
    },
    {
      id: 'darwin',
      title: 'Darwin Office',
      // No street number given intentionally — this is currently a private
      // house, so only the street/suburb is shown.
      addressLines: ['Drysdale Street', 'Parap, NT 0820'],
      phone: '0402 576 431',
      email: 'acoleadams@thinkspatial.com.au',
      showMap: true,
      // Approximate coordinates for Drysdale Street, Parap NT 0820 (no exact
      // house number given, so the map/marker centres on the street).
      coordinates: { lng: 130.8698, lat: -12.4278 }
    }
  ],

  // Named contacts, carried over verbatim from the existing ContactUs.vue.
  people: [
    {
      name: 'Lachlan Beveridge',
      phone: '0417 792 601',
      email: 'lbeveridge@thinkspatial.com.au'
    },
    {
      name: 'Andrew Gaussen',
      phone: '0408 362 395',
      email: 'agaussen@thinkspatial.com.au'
    },
    {
      name: 'Lynden McGregor',
      phone: '0409 799 862',
      email: 'lmcgregor@thinkspatial.com.au'
    }
  ],

  // Contact_Form copy: heading plus per-field labels and the submit/feedback
  // text. Field keys match the ContactEnquiry model (Requirements 7.1, 7.3–7.5).
  form: {
    heading: 'Send us a message',
    fields: {
      name: { label: 'Name', placeholder: 'Your name' },
      email: { label: 'Email', placeholder: 'you@example.com' },
      phone: { label: 'Phone', placeholder: 'Best contact number' },
      subject: { label: 'Subject', placeholder: 'What is your enquiry about?' },
      message: { label: 'Message', placeholder: 'How can we help?' }
    },
    submitLabel: 'Send enquiry',
    successText: 'Thanks — your enquiry has been sent. We will be in touch soon.',
    errorText:
      'Sorry, your enquiry could not be sent. Please try again, or contact us directly by phone or email.'
  },

  /** @type {import('./seo').PageSeo} */
  seo: {
    title: 'Contact Us — ThinkSpatial',
    description:
      'Get in touch with ThinkSpatial for surveying, GIS, UAV mapping, and spatial software enquiries. Offices in Melbourne, Brisbane, and Darwin, or request a quote online.'
  }
}

export default contact
